import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBanners,
  addBanner,
  modifyBanner,
  removeBanner,
} from "store/features/content/contentActions";
import {
  selectBanners,
  selectContentLoading,
  selectContentError,
  selectContentSuccess,
} from "store/features/content/contentSelectors";
import {
  FiUpload,
  FiEdit2,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiPlus,
} from "react-icons/fi";
import { FaImage } from "react-icons/fa";
import Modal from "components/modal/index";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const Banner = () => {
  const dispatch = useDispatch();
  const banners = useSelector(selectBanners);
  const loading = useSelector(selectContentLoading);
  const error = useSelector(selectContentError);
  const success = useSelector(selectContentSuccess);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    image: null,
    displayOrder: "",
  });

  useEffect(() => {
    dispatch(fetchBanners());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchBanners());
      handleCloseModal();
    }
    if (error) {
      showErrorToast(error);
    }
  }, [success, error, dispatch]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCreateBanner = () => {
    const formDataToSend = new FormData();
    formDataToSend.append("image", formData.image);
    formDataToSend.append("displayOrder", formData.displayOrder);
    dispatch(addBanner(formDataToSend));
    showSuccessToast("Banner created successfully!")
  };

  const handleUpdateBanner = () => {
    const updates = {
      displayOrder: formData.displayOrder,
      isActive: selectedBanner?.isActive,
    };
    dispatch(modifyBanner(selectedBanner._id, updates));
    showSuccessToast("Banner updated successfully!")
  };

  const handleDeleteBanner = (id) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      dispatch(removeBanner(id));
       showSuccessToast("Banner deleted successfully!")
    }
  };

  const handleMoveOrder = (id, direction) => {
    const banner = banners.find((b) => b._id === id);
    if (!banner) return;

    const newOrder =
      direction === "up" ? banner.displayOrder - 1 : banner.displayOrder + 1;

    const updates = { displayOrder: newOrder };
    dispatch(modifyBanner(id, updates));
  };

  const handleOpenEditModal = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      displayOrder: banner.displayOrder,
      image: null,
    });
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setImagePreview(null);
    setFormData({
      image: null,
      displayOrder: "",
    });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-red-600">
        Error loading orders: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 h-screen">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-800">App Banner Management</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
        >
          <FiPlus className="mr-2" /> Add New Banner
        </button>
      </div>

      {banners.length === 0 ? (
        <div className="rounded-lg bg-gray-50 py-12 text-center">
          <FaImage className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No banners found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a new banner.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
          >
            Add Banner
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                    Display Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {[...banners].sort((a, b) => a.displayOrder - b.displayOrder).map((banner) => (
                    <tr key={banner._id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-16 w-24 flex-shrink-0">
                            <img
                              className="h-16 w-24 rounded object-cover"
                              src={banner.imageUrl}
                              alt="Banner preview"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="rounded bg-gray-100 px-2 py-1">
                            {banner.displayOrder}
                          </span>
                          <button
                            onClick={() => handleMoveOrder(banner._id, "up")}
                            disabled={banner.displayOrder <= 1}
                            className="p-1 text-gray-500 hover:text-indigo-600 disabled:opacity-50"
                          >
                            <FiArrowUp />
                          </button>
                          <button
                            onClick={() => handleMoveOrder(banner._id, "down")}
                            disabled={banner.displayOrder >= banners.length}
                            className="p-1 text-gray-500 hover:text-indigo-600 disabled:opacity-50"
                          >
                            <FiArrowDown />
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 
                          ${
                            banner.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {banner.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenEditModal(banner)}
                            className="p-2 text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <FiEdit2 className="text-2xl" />
                          </button>
                          <button
                            onClick={() => handleDeleteBanner(banner._id)}
                            className="p-2 text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FiTrash2 className="text-2xl"  />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Banner Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        title="Add New Banner"
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Banner Image
            </label>
            <div className="mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pb-6 pt-5">
              <div className="space-y-1 text-center">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mx-auto h-32 w-full object-contain"
                  />
                ) : (
                  <FaImage className="mx-auto h-12 w-12 text-gray-400" />
                )}
                <div className="mt-2 flex justify-center text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none hover:text-indigo-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="image"
                      type="file"
                      className="sr-only"
                      onChange={handleImageChange}
                      accept="image/*"
                      required
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="displayOrder"
              className="block text-sm font-medium text-gray-700"
            >
              Display Order
            </label>
            <input
              type="number"
              name="displayOrder"
              id="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              min="1"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCloseModal}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateBanner}
              disabled={!formData.image || !formData.displayOrder}
              className="border-transparent rounded-md border bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <FiUpload className="mr-2 inline" />
              Upload Banner
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Banner Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title="Edit Banner"
      >
        {selectedBanner && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Current Banner
              </label>
              <img
                src={selectedBanner.imageUrl}
                alt="Current banner"
                className="h-32 w-full rounded border object-contain"
              />
            </div>

            <div>
              <label
                htmlFor="displayOrder"
                className="block text-sm font-medium text-gray-700"
              >
                Display Order
              </label>
              <input
                type="number"
                name="displayOrder"
                id="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                min="1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedBanner.isActive}
                    onChange={() =>
                      setSelectedBanner({
                        ...selectedBanner,
                        isActive: !selectedBanner.isActive,
                      })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateBanner}
                className="border-transparent rounded-md border bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <FiEdit2 className="mr-2 inline" />
                Update Banner
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Banner;
